import { useToast } from '../useToast'
import { useLensSelectionState } from './useLensSelectionState'
import { Action } from '~/plugins/toast'

/**
 * Responsible for the communication with the middleware (fetching selector data based on product)
 * Resolves SightData Selections to VariantIDs (e.g. {sphere: '0.5', ...} => 12345)
 * These should be persisted in the query params
 */
export const useLensesVariants = (key = 'useLensesVariants') => {
  const { $i18n } = useNuxtApp()
  const scope = effectScope()
  const { logger } = useLogging()
  tryOnScopeDispose(() => scope.stop())
  const {
    data: variantValues,
    fetch: fetchVariantValues,
    fetching,
  } = useFimRpc('getVariantValues', key)
  const {
    data: leftEyeId,
    fetch: fetchLeft,
    fetching: fetchingLeft,
  } = useFimRpc('getVariantFromIdentifiers', `${key}LeftEye`)
  const {
    data: rightEyeId,
    fetch: fetchRight,
    fetching: fetchingRight,
  } = useFimRpc('getVariantFromIdentifiers', `${key}RightEye`)
  const error = ref(false)

  const {
    selectedVariantOptions,
    setLeftLensVariantId,
    setRightLensVariantId,
  } = useLensSelectionState()

  const { alert } = useToast()

  const getSelectorValues = async (productReferenceKey: string) => {
    try {
      await fetchVariantValues({ id: productReferenceKey })
      error.value = false
    } catch (e) {
      error.value = true
      logger.error(e, {
        why: 'fetchVariantValues',
        where: 'useLensesVariants.ts',
        id: productReferenceKey,
      })

      alert($i18n.t('error.connection'), Action.error)
    }
  }

  scope.run(() => {
    if (import.meta.server) {
      return
    }
    watch(leftEyeId, (newValue) => {
      setLeftLensVariantId(
        newValue && newValue !== 'undefined' ? String(newValue) : undefined,
      )
    })
    watch(rightEyeId, (newValue, oldValue) => {
      setRightLensVariantId(
        newValue && newValue !== 'undefined' ? String(newValue) : undefined,
      )
    })
  })

  const isValidVariantValues = (
    selectedVariantOptions: ContactLensValues,
    eyePositionIndex: 'left' | 'right',
  ) =>
    Boolean(
      selectedVariantOptions[eyePositionIndex] &&
        Object.keys(selectedVariantOptions[eyePositionIndex]).length &&
        eyeInputFullySelected(selectedVariantOptions[eyePositionIndex]),
    )

  const fetchIdForSelections = (
    eyePositionIndex: 'left' | 'right',
    productReferenceKey: string | undefined,
  ) => {
    const directionFetcher = {
      left: fetchLeft,
      right: fetchRight,
    }
    if (
      isValidVariantValues(selectedVariantOptions, eyePositionIndex) &&
      productReferenceKey
    ) {
      try {
        return directionFetcher[eyePositionIndex]({
          values: selectedVariantOptions[eyePositionIndex],
          id: productReferenceKey,
        })
      } catch (error: unknown) {
        logger.error(error, {
          why: 'getVariantFromIdentifiers',
          where: 'useLensesVariants.ts',
          eyePositionIndex,
          selectedVariantOptions,
          refrenceKey: productReferenceKey,
        })

        alert($i18n.t('error.connection'), Action.error)
      }
    }
  }

  const informationPopover = computed(() => {
    if (!variantValues.value) {
      return []
    }

    const keys = Object.keys(variantValues.value)
    const basePath = 'pdp.lenses.specifications.'
    const res: { title: string; description: string }[] = []

    for (const key of keys) {
      const entryKey = basePath + key
      if (
        $i18n.te(`${entryKey}.title`) &&
        $i18n.te(`${entryKey}.description`)
      ) {
        res.push({
          title: $i18n.t(`${entryKey}.title`),
          description: $i18n.t(`${entryKey}.description`),
        })
      }
    }

    return res
  })

  return {
    error,
    fetchingVariantIds: computed(
      () => fetchingLeft.value || fetchingRight.value,
    ),
    informationPopover,
    variantValues,
    fetchingSelectorOptions: fetching,
    fetchIdForSelections,
    fetchingLeft,
    fetchingRight,
    getSelectorValues,
    leftEyeId,
    rightEyeId,
  }
}
